import React, { useState, useEffect, memo, useCallback } from 'react';

const RjNotifTable = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const fetchRjData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/rj/getRjToDo');

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const result = await response.json();

      if (Array.isArray(result)) {
        const filteredResult = result
          .filter(item => item.max_advancement < 85)
          .map(item => ({
            ...item,
            operation: item.operation.charAt(0).toUpperCase() + item.operation.slice(1).toLowerCase(),
            sci_sccv: item.sci_sccv.toUpperCase()
          }));
        setData(filteredResult);
      } else setData([])

    } catch (err) {
      console.error('Erreur lors du chargement des données RJ:', err);
      setError(err.message);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRjData();
  }, [fetchRjData]);

  const calculateTooltipPosition = useCallback((mouseX, mouseY) => {
    const tooltipWidth = 280; // Largeur approximative du tooltip
    const tooltipHeight = 150; // Hauteur approximative du tooltip (5 lignes)
    const offset = 10; // Décalage par rapport au curseur
    
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    let x = mouseX + offset;
    let y = mouseY + offset;
    
    // Vérifier si le tooltip dépasse à droite
    if (x + tooltipWidth > viewportWidth) {
      x = mouseX - tooltipWidth - offset;
    }
    
    // Vérifier si le tooltip dépasse en bas
    if (y + tooltipHeight > viewportHeight) {
      y = mouseY - tooltipHeight - offset;
    }
    
    // Vérifier si le tooltip dépasse à gauche (après correction droite)
    if (x < 0) {
      x = offset;
    }
    
    // Vérifier si le tooltip dépasse en haut (après correction bas)
    if (y < 0) {
      y = offset;
    }
    
    return { x, y };
  }, []);

  const handleMouseMove = useCallback((e) => {
    const position = calculateTooltipPosition(e.clientX, e.clientY);
    setTooltipPosition(position);
  }, [calculateTooltipPosition]);

  const renderRjItem = useCallback((item, index) => (
    <div
      key={item.id || index}
      className={`rj-item ${hoveredRow === index ? 'hovered' : ''}`}
      onMouseEnter={() => setHoveredRow(index)}
      onMouseLeave={() => setHoveredRow(null)}
      onMouseMove={handleMouseMove}
    >
      <span className="rj-dossier" style={!item.ndossier ? { color: '#dc3545', fontStyle: 'italic' } : {}}>
        {item.ndossier || 'Indisponible'}
      </span>
      <span className="rj-separator"> - </span>
      <span className="rj-lot" style={!item.operation ? { color: '#dc3545', fontStyle: 'italic' } : {}}>
        {item.operation.length > 40 ? item.operation.slice(0, 40) + '...' : item.operation || 'Indisponible'}
      </span>
      <span className="rj-separator"> - </span>
      <span className="rj-company" style={!item.max_advancement ? { color: '#dc3545', fontStyle: 'italic' } : {}}>
        {item.max_advancement ? `${item.max_advancement}%` : '0%'}
      </span>
    </div>
  ), [hoveredRow, handleMouseMove]);

  if (loading) {
    return (
      <div className="rj-notif-table loading">
        <div className="loading-spinner">Chargement...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rj-notif-table error">
        <div className="error-message">
          Erreur: {error}
        </div>
        <button
          onClick={fetchRjData}
          className="retry-button"
        >
          Réessayer
        </button>
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className="rj-notif-table empty">
        <div className="empty-message">Aucun RJ à faire trouvé.</div>
      </div>
    );
  }

  // console.log("data", data);

  return (
    <div className="rj-notif-table">
      <div className="rj-notif-header">
        <span>RJ à faire ({data.length})</span>
      </div>
      <div className="rj-notif-list">
        {data.map(renderRjItem)}
      </div>
      {hoveredRow !== null && (
        <div
          className="rj-notif-tooltip"
          style={{ 
            left: tooltipPosition.x, 
            top: tooltipPosition.y,
            position: 'fixed'
          }}
        >
          <p><b>Dossier:</b> <span style={!data[hoveredRow].ndossier ? { color: '#dc3545', fontStyle: 'italic' } : {}}>{data[hoveredRow].ndossier || 'Indisponible'}</span></p>
          <p><b>Avancement:</b> <span style={!data[hoveredRow].max_advancement ? { color: '#dc3545', fontStyle: 'italic' } : {}}>{data[hoveredRow].max_advancement ? `${data[hoveredRow].max_advancement}%` : '0%'}</span></p>
          <p><b>SCI SCCV:</b> <span style={!data[hoveredRow].sci_sccv ? { color: '#dc3545', fontStyle: 'italic' } : {}}>{data[hoveredRow].sci_sccv || 'Indisponible'}</span></p>
          <p><b>Opération:</b> <span style={!data[hoveredRow].operation ? { color: '#dc3545', fontStyle: 'italic' } : {}}>{data[hoveredRow].operation || 'Indisponible'}</span></p>
          <p><b>Adresse:</b> <span style={!data[hoveredRow].adresse_op ? { color: '#dc3545', fontStyle: 'italic' } : {}}>{`${data[hoveredRow].adresse_op}, ${data[hoveredRow].cp_op} - ${data[hoveredRow].ville_op}` || 'Indisponible'}</span></p>
        </div>
      )}
    </div>
  );
};

export default memo(RjNotifTable);